import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Package, ArrowRight, Zap, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const lineVariants = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 1, ease: "easeInOut" },
  },
};

// Sprinkle/Confetti component
const Sprinkle = ({ index }) => {
  const randomDelay = Math.random() * 0.5;
  const randomDuration = 2 + Math.random() * 1;
  const randomX = Math.random() * window.innerWidth;
  const randomRotation = Math.random() * 360;

  return (
    <motion.div
      key={index}
      initial={{
        opacity: 1,
        y: -10,
        x: randomX,
        rotate: 0,
      }}
      animate={{
        opacity: 0,
        y: 200,
        rotate: randomRotation,
      }}
      transition={{
        duration: randomDuration,
        delay: randomDelay,
        ease: "easeIn",
      }}
      className="fixed pointer-events-none"
    >
      <div
        className={`${
          index % 3 === 0 ? "bg-royal" : index % 3 === 1 ? "bg-emerald" : "bg-yellow-400"
        } w-2 h-2 rounded-full`}
      />
    </motion.div>
  );
};

// Flying boxes component
const FlyingBox = ({ index }) => {
  const positions = [
    { top: "10%", left: "5%" },
    { top: "15%", right: "8%" },
    { top: "20%", left: "12%" },
    { top: "30%", right: "15%" },
    { bottom: "25%", left: "8%" },
    { bottom: "20%", right: "10%" },
  ];
  const position = positions[index % positions.length];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0, rotate: -180 }}
      animate={{ opacity: [0.3, 0.6, 0], scale: [0, 1, 0], rotate: 180 }}
      transition={{
        duration: 3 + index * 0.3,
        delay: index * 0.2,
        repeat: Infinity,
        repeatDelay: 2,
      }}
      className="absolute pointer-events-none"
      style={position}
    >
      <div
        className={`${
          index % 3 === 0
            ? "bg-royal/20"
            : index % 3 === 1
              ? "bg-emerald/20"
              : "bg-yellow-400/20"
        } w-6 h-6 lg:w-8 lg:h-8 rounded-lg border border-current`}
      />
    </motion.div>
  );
};

// Burst particles component
const BurstParticle = ({ index }) => {
  const angle = (index / 20) * Math.PI * 2;
  const distance = 100;
  const tx = Math.cos(angle) * distance;
  const ty = Math.sin(angle) * distance;

  return (
    <motion.div
      key={index}
      className="fixed pointer-events-none"
      initial={{ opacity: 1, x: 0, y: 0 }}
      animate={{
        opacity: 0,
        x: tx,
        y: ty,
      }}
      transition={{
        duration: 1.5,
        delay: 0.5,
        repeat: Infinity,
        repeatDelay: 3,
      }}
      style={{
        top: "50%",
        left: "50%",
        marginLeft: "-2px",
        marginTop: "-2px",
      }}
    >
      <Star
        size={4}
        fill={index % 3 === 0 ? "rgb(25, 39, 92)" : index % 3 === 1 ? "rgb(16, 185, 129)" : "rgb(234, 179, 8)"}
        color={index % 3 === 0 ? "rgb(25, 39, 92)" : index % 3 === 1 ? "rgb(16, 185, 129)" : "rgb(234, 179, 8)"}
      />
    </motion.div>
  );
};

export default function LaunchAnnouncement() {
  const [shouldShow, setShouldShow] = useState(false);
  const [isExploring, setIsExploring] = useState(false);
  const [sprinkles, setSprinkles] = useState([]);

  useEffect(() => {
    // Always show on launch day - the parent App component controls visibility
    setShouldShow(true);
    // Generate sprinkles for animation
    setSprinkles(Array.from({ length: 50 }));
  }, []);

  const handleExplore = () => {
    setIsExploring(true);
    // Hide launch page and navigate to app
    setTimeout(() => {
      window.location.href = "/?app=true";
    }, 800);
  };

  if (!shouldShow) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-b from-white via-slate-50 to-white overflow-y-auto">
      {/* Animated background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating gradient orbs */}
        <motion.div
          animate={{
            y: [0, 30, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-72 h-72 bg-royal/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, -30, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-20 right-10 w-72 h-72 bg-emerald/5 rounded-full blur-3xl"
        />

        {/* Flying celebration boxes */}
        {Array.from({ length: 6 }).map((_, i) => (
          <FlyingBox key={`box-${i}`} index={i} />
        ))}

        {/* Confetti sprinkles */}
        {sprinkles.map((_, i) => (
          <Sprinkle key={`sprinkle-${i}`} index={i} />
        ))}

        {/* Burst particles */}
        {Array.from({ length: 20 }).map((_, i) => (
          <BurstParticle key={`burst-${i}`} index={i} />
        ))}

        {/* Floating sparkles around page */}
        {Array.from({ length: 12 }).map((_, i) => (
          <motion.div
            key={`sparkle-${i}`}
            className="absolute"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
            transition={{
              duration: 2 + i * 0.15,
              delay: i * 0.1,
              repeat: Infinity,
              repeatDelay: 1,
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          >
            <Sparkles className="w-4 h-4 text-royal/40" />
          </motion.div>
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full min-h-screen flex flex-col">
        {/* Top section */}
        <div className="flex-1 flex flex-col items-center justify-center px-5 py-20">
          <motion.div className="max-w-2xl w-full space-y-12 text-center" variants={containerVariants} initial="hidden" animate="visible">
            {/* Logo with pulse */}
            <motion.div variants={itemVariants} className="flex justify-center">
              <motion.div
                className="flex items-center gap-3"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <motion.div
                  className="grid place-items-center h-14 w-14 rounded-xl bg-gradient-to-br from-navy to-royal text-white shadow-xl"
                  animate={{ boxShadow: ["0 0 0 0 rgba(25, 39, 92, 0.4)", "0 0 0 15px rgba(25, 39, 92, 0)"] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Package size={28} strokeWidth={2} />
                </motion.div>
                <span className="font-heading text-3xl font-extrabold tracking-tight">
                  Elitz<span className="text-royal">Packs</span>
                </span>
              </motion.div>
            </motion.div>

            {/* Animated line */}
            <motion.div variants={lineVariants} className="mx-auto w-12 h-1 bg-gradient-to-r from-navy via-royal to-emerald rounded-full" />

            {/* Heading with character animation */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-5xl md:text-6xl font-heading font-extrabold text-slate-900">
                ✨ A New Chapter Begins
              </h1>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="mx-auto h-1 w-24 bg-gradient-to-r from-royal to-emerald rounded-full origin-left"
              />
              <p className="text-xl text-slate-600">Official Launch of ElitzPackz Website</p>
            </motion.div>

            {/* Blessing with badge effect */}
            <motion.div variants={itemVariants} className="space-y-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="inline-block mx-auto px-4 py-2 bg-gradient-to-r from-royal/10 to-emerald/10 rounded-full border border-royal/20"
              >
                <p className="text-base text-slate-600 font-medium">With the Blessings of</p>
              </motion.div>
              <motion.p
                animate={{ color: ["rgb(25, 39, 92)", "rgb(107, 39, 130)", "rgb(25, 39, 92)"] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="text-2xl font-semibold"
              >
                Rekha Ma&apos;am & Rupesh Sir
              </motion.p>
              <motion.p className="text-sm font-mono font-semibold text-royal tracking-widest">
                AUGUST 2026
              </motion.p>
            </motion.div>
          </motion.div>
        </div>

        {/* Content section */}
        <div className="w-full bg-white/50 backdrop-blur-sm border-t border-slate-200">
          <div className="max-w-2xl mx-auto px-5 py-16 space-y-12">
            {/* Main message */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="space-y-6 text-center"
            >
              <motion.p variants={itemVariants} className="text-lg leading-relaxed text-slate-700">
                With immense gratitude and happiness, we are proud to announce the official launch of the{" "}
                <motion.span
                  className="font-bold bg-gradient-to-r from-navy to-royal bg-clip-text text-transparent"
                  animate={{ backgroundPosition: ["0% center", "100% center", "0% center"] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  ElitzPackz Website
                </motion.span>
                .
              </motion.p>

              <motion.p variants={itemVariants} className="text-lg leading-relaxed text-slate-700">
                A new digital home for our vision, our products, our creativity, and our commitment to delivering{" "}
                <span className="font-semibold text-royal">quality packaging solutions for every business</span>.
              </motion.p>
            </motion.div>


            {/* Gratitude section */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="space-y-3 text-center"
            >
              <motion.h3 variants={itemVariants} className="text-lg font-semibold text-slate-900">
                With Gratitude
              </motion.h3>
              <motion.p variants={itemVariants} className="text-base text-slate-600">
                We sincerely acknowledge the blessings, encouragement, guidance, and support of
              </motion.p>
              <motion.p variants={itemVariants} className="text-lg font-semibold bg-gradient-to-r from-navy to-royal bg-clip-text text-transparent">
                Rekha & Rupesh
              </motion.p>
            </motion.div>

            {/* Vision section with icons */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="space-y-4 text-center"
            >
              <motion.div variants={itemVariants} className="flex items-center justify-center gap-2">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="h-1 w-8 bg-royal rounded-full" />
                <p className="text-2xl font-bold text-slate-900">A New Website.</p>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="h-1 w-8 bg-royal rounded-full" />
              </motion.div>
              <motion.div variants={itemVariants} className="flex items-center justify-center gap-2">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="h-1 w-8 bg-royal rounded-full" />
                <p className="text-2xl font-bold text-slate-900">A New Beginning.</p>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="h-1 w-8 bg-royal rounded-full" />
              </motion.div>
              <motion.div variants={itemVariants} className="flex items-center justify-center gap-2">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="h-1 w-8 bg-emerald rounded-full" />
                <p className="text-2xl font-bold text-emerald">A Bigger Vision.</p>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="h-1 w-8 bg-emerald rounded-full" />
              </motion.div>
              <motion.p variants={itemVariants} className="text-3xl font-bold text-slate-900 pt-4">
                Welcome to ElitzPackz.
              </motion.p>
              <motion.p variants={itemVariants} className="text-lg italic text-slate-600 pt-2">
                Where every package tells your brand&apos;s story.
              </motion.p>
            </motion.div>

            {/* CTA Button with loading state */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="flex justify-center pt-8"
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  onClick={handleExplore}
                  disabled={isExploring}
                  size="lg"
                  className="group relative bg-gradient-to-r from-navy to-royal hover:from-royal hover:to-navy text-white font-bold py-7 px-12 text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-70"
                >
                  {isExploring ? (
                    <motion.div className="flex items-center gap-2">
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} className="inline-block">
                        <Zap size={22} />
                      </motion.div>
                      Launching...
                    </motion.div>
                  ) : (
                    <>
                      Explore ElitzPackz
                      <motion.div animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                        <ArrowRight className="ml-3" size={22} />
                      </motion.div>
                    </>
                  )}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
